import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import { getEntityTitle } from "../../../common/files/fileDetailsHelper";
import type { TagSettingsDialogState } from "./dialogState";

interface Props {
  dialogState: TagSettingsDialogState;
  onCancel: () => void;
  onSubmit: (catalogueId: string) => void;
}

const deleteFileTag = withErrorHandling(adminApi.deleteFileTag);

const DeleteTagDialog = ({ dialogState, onCancel, onSubmit }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const { catalogueId } = dialogState.form;
    if (!catalogueId) {
      return;
    }

    setSaving(true);
    const [resp, error] = await deleteFileTag(catalogueId);
    if (error || !resp.result) {
      logError(error, "deleteFileTag");
      sendNotificationError(error?.message || "Could not delete file tag");
    } else {
      sendNotification(
        `Tag "${dialogState.form.name}" deleted successfully from entity ${
          dialogState.form.entityType ? getEntityTitle(dialogState.form.entityType) : ""
        }`
      );
      onSubmit(catalogueId);
    }
    setSaving(false);
  };

  return (
    <SmallDialog open={dialogState.openDialog === "delete"} onClose={onCancel}>
      <DialogTitle>Delete Tag</DialogTitle>
      <DialogCloseButton onClick={onCancel} />
      <DialogContent sx={(t) => ({ px: t.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80, my: 2 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">{`Delete "${dialogState.form.name}"`}</Typography>
            <Typography>Are you sure you want to delete the tag?</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onCancel}>
        <Button variant="outlined" color="error" loading={saving} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteTagDialog;
